import { relations, sql } from 'drizzle-orm';
import { index, jsonb, pgEnum, pgTable, text, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import {} from '../../util/schemas.js';
import { uuidv7 } from './utils/uuidv7.js';
import { productionLocations } from './pharmacies.js';
import { batches } from './stocks.js';
import { patientTherapySequence } from './patient-therapy.js';
/** What a picked batch was used as in one prescription. */
export const OrderBatchCategory = z.enum(['ACTIVE_SUBSTANCE', 'CARRIER', 'SOLVENT', 'CONSUMABLE']);
export const orderBatchCategoryEnum = pgEnum('order_batch_category', [
    OrderBatchCategory.enum.ACTIVE_SUBSTANCE,
    OrderBatchCategory.enum.CARRIER,
    OrderBatchCategory.enum.SOLVENT,
    OrderBatchCategory.enum.CONSUMABLE,
]);
export const orders = pgTable('order', {
    id: uuidv7('id').primaryKey(),
    internalId: text('internal_id').notNull(),
    productionLocationId: uuid('production_location_id')
        .notNull()
        .references(() => productionLocations.id, { onDelete: 'restrict' }),
    distributedOrders: jsonb('distributed_orders').$type().default({}),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_order_production_location').on(t.productionLocationId),
    // the orders list pages by creation time
    index('idx_order_created')
        .on(t.createdAt, t.id)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const orderRelations = relations(orders, ({ one, many }) => ({
    productionLocation: one(productionLocations, {
        fields: [orders.productionLocationId],
        references: [productionLocations.id],
    }),
    sequences: many(patientTherapySequence),
    batches: many(orderBatches),
}));
export const orderBatches = pgTable('order_batch', {
    id: uuidv7('id').primaryKey(),
    orderId: uuid('order_id')
        .notNull()
        .references(() => orders.id, { onDelete: 'cascade' }),
    batchId: uuid('batch_id')
        .notNull()
        .references(() => batches.id, { onDelete: 'cascade' }),
    /** the prescription that used this batch; legacy rows from before CY-382 have none */
    therapySequenceId: uuid('therapy_sequence_id').references(() => patientTherapySequence.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
    }),
    category: orderBatchCategoryEnum('category'),
    ...timestamps(),
    ...userActions(),
}, table => [
    index('order_batch_therapy_sequence_idx').on(table.therapySequenceId),
    index('idx_order_batch_order').on(table.orderId),
    index('idx_order_batch_batch').on(table.batchId),
]);
export const orderBatchRelations = relations(orderBatches, ({ one }) => ({
    order: one(orders, {
        fields: [orderBatches.orderId],
        references: [orders.id],
    }),
    batch: one(batches, {
        fields: [orderBatches.batchId],
        references: [batches.id],
    }),
    therapySequence: one(patientTherapySequence, {
        fields: [orderBatches.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
}));
//# sourceMappingURL=production-orders.js.map